import { AutocompleteChangeReason } from "@mui/material";
import { EMPTY_URL } from "csi/constants";
import { PreferenceView } from "csi/prefs/PreferenceView";
import csiPrefs from "csi/prefs/_base";
import { sessionStore } from "csi/state/rootStore";
import { arrayMove } from "csi/utility/arrayUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ComponentItem } from "designSystem/types/component-item-types";
import { flatten } from "lodash";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { DropTargetMonitor } from "react-dnd";
import { reorderFavorites } from "src/c8-platform/service/customViewBuilderService";
import { C8Node } from "src/c8-platform/type/node";
import { cnl } from "src/c8-platform/type/value";
import CustomViewDialogStore from "../CustomViewDialogStore";
import { ViewCategory } from "./CustomViewDialogLeftPanelAccordion";
import { CustomViewItem } from "./CustomViewItem";
import { NonGlobalCustomView } from "./customViewDialogLeftPanelService";

export class CustomViewStore extends ReactionStore {
	/** being duplicated CustomViewItem */
	beingDuplicatedCustomViewItem: CustomViewItem;

	/** indicate the action of being duplicated CustomViewItem is SaveAs or not   */
	beingDuplicatedCustomViewItemIsSaveAs: boolean;

	@observable.ref private _currentSelectedUser: ComponentItem;

	/** CustomViewDialogStore */
	readonly #customViewDialogStore: CustomViewDialogStore;

	/** item for duplicated custom view to show in the left panel */
	duplicatedCustomViewItem: CustomViewItem;

	/** holding the duplicated PreferenceView, which needs to be saved to the database */
	duplicatedPreferenceView: PreferenceView = null;

	/** favorite views map, including names */
	readonly favoriteItems = observable.array<CustomViewItem>([]);

	/** flag to indicate whether the favorite list is loading */
	@observable isLoadingFavorites: boolean = false;

	/** initial role based custom views */
	readonly initialRoleItems: NonGlobalCustomView[];

	/** person owned custom views */
	@observable.ref personalItems: NonGlobalCustomView[];

	/** search text to search by */
	@observable searchText: string = "";

	/** currently selected PreferenceView */
	@observable.ref selectedPreferenceView: PreferenceView;

	/** overall global custom views, including names */
	readonly systemWideItems = observable.array<CustomViewItem>([]);

	/** all views map */
	@computed get allViewMap(): Map<cnl, CustomViewItem> {
		const { nonPersonalViewMap, personalItems } = this;
		const personalViewMap = new Map<cnl, CustomViewItem>();

		for (const personalItem of personalItems) {
			for (const item of personalItem.viewItems) {
				personalViewMap.set(item.url, item);
			}
		}

		return new Map([...nonPersonalViewMap.entries(), ...personalViewMap.entries()]);
	}

	/** indicate if the OOTB default view presents */
	get containsOOTBDefaultView(): boolean {
		return this.systemWideItemSet.has(EMPTY_URL);
	}

	/** current selected user  */
	get currentSelectedUser(): ComponentItem {
		const { userList, _currentSelectedUser } = this;

		return _currentSelectedUser || userList.find((user) => user.id == sessionStore.userUrl);
	}

	/** default CustomViewItem */
	get defaultCustomView(): CustomViewItem {
		const { customViewContext, defaults } = this.#customViewDialogStore;
		const { nonPersonalViewMap } = this;
		const targetCustomViewUrl = defaults.get(customViewContext, EMPTY_URL);
		return nonPersonalViewMap.get(targetCustomViewUrl);
	}

	/** favorite views cnls set */
	get favoriteItemSet(): Set<cnl> {
		return new Set(this.favoriteItems.map(({ url }) => url));
	}

	/** filtered favorite custom view items */
	get filteredFavoriteItems(): CustomViewItem[] {
		const { searchText } = this;
		return this.favoriteItems.filter(({ searchViewByName }) => searchViewByName(searchText));
	}

	/** filtered personal item, if the current user is admin */
	get filteredPersonalItem(): NonGlobalCustomView | undefined {
		const { personalItems } = this;

		const { currentSelectedUser: { id } } = this;
		return personalItems.find((item) => item.url === id);
	}

	/** filtered system wide custom view items */
	get filteredSystemWideItems(): CustomViewItem[] {
		const { searchText } = this;
		return this.systemWideItems.filter((view) => view.searchViewByName(searchText));
	}

	/** indicate if the current user has admin access */
	get hasAdminAccess(): boolean {
		return csiPrefs.hasAdminAccess();
	}

	/** indicate if the current login user is the owner of this currently selected personal view */
	get isViewingTheViewsOfOtherPerson(): boolean {
		const { personalViewIsBeingSelected, currentSelectedUser } = this;

		return personalViewIsBeingSelected && currentSelectedUser.id != sessionStore.userUrl;
	}

	/** names map to look up */
	get names(): Record<cnl, string> {
		const nameMap = {};

		for (const [cnl, customViewItem] of this.allViewMap) {
			nameMap[cnl] = customViewItem.name;
		}

		return nameMap;
	}

	/** names Set to look up */
	get nameSet(): Set<string> {
		return new Set(Object.values(this.names));
	}

	/** view map of system wide and role based */
	@computed get nonPersonalViewMap(): Map<cnl, CustomViewItem> {
		const { systemWideItems, roleItems } = this;

		const viewMap = new Map<cnl, CustomViewItem>();

		for (const item of systemWideItems) {
			viewMap.set(item.url, item);
		}

		for (const roleItem of roleItems) {
			for (const item of roleItem.viewItems) {
				viewMap.set(item.url, item);
			}
		}

		return viewMap;
	}

	/** personal views cnls set */
	get personalItemSet(): Set<cnl> {
		return new Set(flatten(this.personalItems.map((personal) => personal.viewItems.map(({ url }) => url))));
	}

	/** indicate if the current selected view is a personal view */
	get personalViewIsBeingSelected(): boolean {
		return this.selectedCustomViewItem?.category === ViewCategory.PERSONAL;
	}

	/** filtered role based custom views */
	get roleItems(): NonGlobalCustomView[] {
		return this.initialRoleItems.filter((item) => item.viewItems.length > 0);
	}

	/** role based views cnls set */
	get roleItemSet(): Set<cnl> {
		const { roleItems } = this;
		const result = new Set<cnl>();
		roleItems.forEach((role) => role.viewItems.forEach(({ url }) => result.add(url)));

		return result;
	}

	/** indicate if disable SaveAs button in the header */
	get saveNCloseButtonDisabled(): boolean {
		return this.saveButtonDisabled;
	}

	/** indicate if disable Save button */
	get saveButtonDisabled(): boolean {
		const {
			settingStore: { nameErrorRes, isViewSettingsDirty },
			viewIsDirty,
		} = this.#customViewDialogStore;
		// data management store can be undefined in property views
		const hasInvalidRule = this.#customViewDialogStore.dataManagementStore?.hasInvalidRule;

		return !(viewIsDirty || isViewSettingsDirty)
			|| !!nameErrorRes
			|| hasInvalidRule
			|| this.#customViewDialogStore.isSaving;
	}

	/** selected CustomViewItem instance, the value could be undefined for the intermediate state, for example, delete action where multiple async calls get involved */
	get selectedCustomViewItem(): CustomViewItem | undefined {
		const { allViewMap } = this;
		const { selectedCustomViewCnl } = this.#customViewDialogStore;

		return allViewMap.get(selectedCustomViewCnl);
	}

	/** indicate if show Discard button in the header */
	get showDiscard(): boolean {
		return this.selectedCustomViewItem?.showDiscard;
	}

	/** indicate if show the personal view section */
	get showPersonalViewSection(): boolean {
		return (this.filteredPersonalItem?.viewItems.length > 0) || this.hasAdminAccess;
	}

	/** indicate if show Save button in the header */
	get showSave(): boolean {
		const { selectedPreferenceView, duplicatedCustomViewItem } = this;
		return selectedPreferenceView.canEdit() || !!duplicatedCustomViewItem;
	}

	/** indicate if show Save&Close button in the header */
	get showSaveAndClose(): boolean {
		return this.showSave;
	}

	/** indicate if show SaveAs as primary button in the header */
	get showSaveAsAsPrimary(): boolean {
		const { duplicatedCustomViewItem, selectedPreferenceView } = this;
		return !selectedPreferenceView.canEdit() && !duplicatedCustomViewItem;
	}

	/** indicate if show SaveAs in the more actions in the header */
	get showSaveAsInMore(): boolean {
		const { duplicatedCustomViewItem, showSaveAsAsPrimary } = this;
		return !showSaveAsAsPrimary && !duplicatedCustomViewItem;
	}

	/** indicate if show the user selector */
	get showUserSelector(): boolean {
		return this.hasAdminAccess;
	}

	/** system wide views cnls set */
	get systemWideItemSet(): Set<cnl> {
		return new Set(this.systemWideItems.map(({ url }) => url));
	}

	/** user list to switch from */
	@computed get userList(): ComponentItem[] {
		return this.personalItems.map(({ url, name }) => ({ id: url, name }));
	}

	/** add favorite custom view */
	@action addFavoriteCustomViewItem = (favoriteCustomView: CustomViewItem): void => {
		this.favoriteItems.push(favoriteCustomView);
	};

	/** pull OOTB custom view in and mark it selected  */
	@action addOOTBCustomView = (): CustomViewItem => {
		const { names, systemWideItems } = this;
		names[EMPTY_URL] = this.#customViewDialogStore.loc.resource("ViewDefault");
		const OOTBCustomViewItem = new CustomViewItem(
			EMPTY_URL,
			names[EMPTY_URL],
			ViewCategory.PUBLIC,
			this.#customViewDialogStore,
			null,
			true,
			false,
		);

		systemWideItems.unshift(OOTBCustomViewItem);
		return OOTBCustomViewItem;
	};

	/** discard the duplicated custom view item */
	@action cancelDuplicateCustomView = (): void => {
		const { clearDuplicated, roleItemSet, personalItemSet, systemWideItems } = this;

		if (roleItemSet.has(null)) {
			// role views, could be in multiple groups
			const { initialRoleItems } = this;

			initialRoleItems.forEach((role) => {
				const target = role.viewItems.find((item) => item.url === null);
				role.viewItems.remove(target);
			});
		} else if (personalItemSet.has(null)) {
			const { personalItems } = this;
			personalItems.some((personal) => {
				const target = personal.viewItems.find((item) => item.url === null);

				if (target) {
					personal.viewItems.remove(target);
					return true;
				}

				return false;
			});
		} else {
			const target = systemWideItems.find((item) => item.url === null);
			systemWideItems.remove(target);
		}

		clearDuplicated();
	};

	/** clear the pending custom view item if any */
	clearDuplicated = (): void => {
		this.duplicatedCustomViewItem = null;
		this.duplicatedPreferenceView = null;
	};

	/** delete the custom views from each level */
	@action deleteCustomViewItem = (beingDeletedCustomView: CustomViewItem): void => {
		const { initialRoleItems, personalItems, removeFavoriteCustomViewItem, systemWideItems } = this;

		initialRoleItems.forEach((role) => {
			role.viewItems.remove(beingDeletedCustomView);
		});

		personalItems.forEach((personal) => {
			personal.viewItems.remove(beingDeletedCustomView);
		});

		systemWideItems.remove(beingDeletedCustomView);
		removeFavoriteCustomViewItem(beingDeletedCustomView);
	};

	/** Discard the duplicated custom view  */
	discardDuplicatedCustomView = (): void => {
		this.selectedCustomViewItem.discard();
	};

	/** drop favorite view and save it to the database */
	@action dropFavorite = async (customViewItem: CustomViewItem, monitor: DropTargetMonitor, targetIndex: number, groupId: string): Promise<CustomViewItem> => {
		const { favoriteItems } = this;
		const { customViewContainerUrl } = this.#customViewDialogStore;
		const originalIndex = favoriteItems.indexOf(customViewItem);

		if (originalIndex === targetIndex) {
			return;
		}

		arrayMove(favoriteItems, originalIndex, targetIndex, false);

		// save
		await reorderFavorites(sessionStore.userUrl, customViewContainerUrl, Array.from(this.favoriteItemSet));

		return customViewItem;
	};

	/** generate the view name */
	private generateViewName = (view: CustomViewItem): string => {
		const { nameSet } = this;
		let viewName = view.name;

		while (nameSet.has(viewName)) {
			viewName = this.#customViewDialogStore.loc.resource("CustomViewCopyName", [viewName]);
		}

		return viewName;
	};

	/** create/duplicate a temporary PreferenceView object and linked the CustomViewItem to it as well */
	private duplicateCustomViewFromTargetView = (target: CustomViewItem, isSaveAs: boolean, isTransferring: boolean): CustomViewItem => {
		const customViewDialogStore = this.#customViewDialogStore;
		const { generateViewName } = this;

		// create custom view so that user can navigate to 
		const { cache, newInstanceFromNode, optionValues, settingStore: { prefName: customViewName } } = customViewDialogStore;
		const cvNode = cache.get(target.url);
		const cv = newInstanceFromNode(cvNode);
		cv.urlNode = null;

		// in the case of internal transferring, keep the original name
		let prefName = isTransferring ? customViewName : generateViewName(target);

		if (isSaveAs) {
			// in the case of SaveAs, need to use the changed version vs the original version
			cv.values = optionValues;

			// in the case of SaveAs action from the toolbar, we want include "Copy"
			// if the user doesn't change the name, we include "Copy"
			if (!customViewDialogStore.forceDuplicate && customViewName !== target.name) {
				prefName = customViewName;
			}
		}

		if (!this.hasAdminAccess) {
			// make the custom view personal if user is just custom view editor, no matter whether it's role-based or system-wide view
			cv.setCurrentUser();
		}

		cv.values.prefName = cv.name = prefName;
		this.duplicatedPreferenceView = cv;

		const tempDuplicatedItem = new CustomViewItem(
			null,
			prefName,
			target.category,
			customViewDialogStore,
			target.group,
			target.selected,
			false,
		);

		this.duplicatedCustomViewItem = tempDuplicatedItem;

		return tempDuplicatedItem;
	};

	/** duplicate view as personal group if the user is not admin */
	private duplicateViewToPersonalForEditor = (duplicatedViewItem: CustomViewItem): void => {
		const { personalItems } = this;

		if (!personalItems.length) {
			this.personalItems = [
				new NonGlobalCustomView(
					sessionStore.userUrl,
					sessionStore.userName,
					[duplicatedViewItem],
					this.#customViewDialogStore,
				),
			];
		} else {
			personalItems[0].viewItems.push(duplicatedViewItem);
		}
	};

	/** duplicate the custom view  */
	@action duplicateCustomViewItem = (beingDuplicatedCustomView: CustomViewItem, isSaveAs: boolean, isTransferring: boolean): CustomViewItem => {
		this.beingDuplicatedCustomViewItem = beingDuplicatedCustomView;
		this.beingDuplicatedCustomViewItemIsSaveAs = isSaveAs;
		const { duplicateCustomViewFromTargetView, duplicateViewToPersonalForEditor, hasAdminAccess, roleItemSet, personalItemSet } = this;
		const { url } = beingDuplicatedCustomView;

		let tempDuplicatedItem: CustomViewItem = null;

		if (roleItemSet.has(url)) {
			// role views, could be in multiple groups, so, may need to duplicate multiples
			const { initialRoleItems } = this;

			if (hasAdminAccess) {
				initialRoleItems.forEach((role) => {
					const target = role.viewItems.find((item) => item.url === url);

					if (target) {
						tempDuplicatedItem ||= duplicateCustomViewFromTargetView(target, isSaveAs, isTransferring);
						role.viewItems.push(tempDuplicatedItem);
					}
				});
			} else {
				// make the custom view personal if view is role-based and user is just custom view editor
				initialRoleItems.some((role) => {
					const target = role.viewItems.find((item) => item.url === url);

					if (target) {
						tempDuplicatedItem = duplicateCustomViewFromTargetView(target, isSaveAs, isTransferring);
						duplicateViewToPersonalForEditor(tempDuplicatedItem);
						return true;
					}
				});
			}
		} else if (personalItemSet.has(url)) {
			// personal view, only duplicate one
			const { personalItems, userList } = this;
			const { userUrl } = sessionStore;
			personalItems.some((personal) => {
				const target = personal.viewItems.find((item) => item.url === url);

				if (target) {
					tempDuplicatedItem = duplicateCustomViewFromTargetView(target, isSaveAs, isTransferring);

					if (this.duplicatedPreferenceView.isCurrentUser()) {
						personal.viewItems.push(tempDuplicatedItem);
					} else {
						// if the user performing duplicate action is not the same as the original user, the view will be owned by the current user rather then the original user
						this.duplicatedPreferenceView.setCurrentUser();
						const currentUserPersonalItems = personalItems.find(({ url }) => url === userUrl);

						currentUserPersonalItems.viewItems.push(tempDuplicatedItem);
						this._currentSelectedUser = userList.find(({ id }) => id === userUrl);
					}

					return true;
				}

				return false;
			});
		} else {
			// system-wide view, only duplicate one
			const { systemWideItems } = this;
			const target = systemWideItems.find((item) => item.url === url);
			tempDuplicatedItem = duplicateCustomViewFromTargetView(target, isSaveAs, isTransferring);

			if (!hasAdminAccess) {
				// make the custom view personal if view is system-wide and user is just custom view editor
				duplicateViewToPersonalForEditor(tempDuplicatedItem);
			} else {
				systemWideItems.push(tempDuplicatedItem);
			}
		}

		return tempDuplicatedItem;
	};

	/** handler to refresh the store */
	@action onRefreshStore = (newCustomViewStore: CustomViewStore, isSaving: boolean): void => {
		const {
			beingDuplicatedCustomViewItem,
			beingDuplicatedCustomViewItemIsSaveAs,
			duplicatedCustomViewItem,
			selectedPreferenceView,
			currentSelectedUser,
		} = this;

		if (duplicatedCustomViewItem && !isSaving) {
			newCustomViewStore.duplicateCustomViewItem(beingDuplicatedCustomViewItem, beingDuplicatedCustomViewItemIsSaveAs, true);
		}

		newCustomViewStore.selectedPreferenceView = selectedPreferenceView;
		newCustomViewStore._currentSelectedUser = currentSelectedUser;
	};

	/** refresh the selected state once the selected custom view changes */
	@action refreshSelectedItems = (selectedCustomViewUrl: cnl): void => {
		const { favoriteItems, personalItems, roleItems, systemWideItems } = this;

		personalItems.forEach((personal) => personal.viewItems.forEach((view) => {
			view.selected = view.url === selectedCustomViewUrl;
		}));

		roleItems.forEach((role) => role.viewItems.forEach((view) => {
			view.selected = view.url === selectedCustomViewUrl;
		}));

		systemWideItems.forEach((view) => {
			view.selected = view.url === selectedCustomViewUrl;
		});

		favoriteItems.forEach((view) => {
			view.selected = view.url === selectedCustomViewUrl;
		});
	};

	/** refresh the favorite state once the favorite custom view changes */
	@action refreshFavoriteItems = (): void => {
		const { favoriteItemSet, personalItems, roleItems, systemWideItems } = this;

		personalItems.forEach((personal) => personal.viewItems.forEach((view) => {
			view.isFavorite = favoriteItemSet.has(view.url);
		}));

		roleItems.forEach((role) => role.viewItems.forEach((view) => {
			view.isFavorite = favoriteItemSet.has(view.url);
		}));

		systemWideItems.forEach((view) => {
			view.isFavorite = favoriteItemSet.has(view.url);
		});
	};

	/** remove the favorite custom view item */
	@action removeFavoriteCustomViewItem = (favoriteCustomView: CustomViewItem): void => {
		const { url } = favoriteCustomView;
		this.favoriteItems.some((item) => {
			if (item.url === url) {
				this.favoriteItems.remove(item);
			}
		});
	};

	/** remove OOTB default view  */
	@action removeOOTBCustomView = (): void => {
		const OOTBView = this.nonPersonalViewMap.get(EMPTY_URL);
		this.systemWideItems.remove(OOTBView);
	};

	/** saveAs the current selected custom view  */
	saveAsCurrent = (): void => {
		const { selectedCustomViewItem } = this;
		selectedCustomViewItem.saveAs();
	};

	/** save the current selected custom view and refresh the custom view store as needed  */
	saveCurrentWithRefresh = async (): Promise<void> => {
		await this.saveCurrent(false);
	};

	/** save the current selected custom view  */
	saveCurrent = async (isSaveAndClose: boolean): Promise<C8Node> => {
		const { saveCustomView, selectedCustomViewCnl } = this.#customViewDialogStore;
		return await saveCustomView(selectedCustomViewCnl, isSaveAndClose);
	};

	/** set whether the favorite list is loading */
	@action setIsLoadingFavorites = (isLoading: boolean): void => {
		this.isLoadingFavorites = isLoading;
	};

	/** set search text */
	@action setSearchText = (text: string): void => {
		this.searchText = text;
	};

	/** switch user to show the views of it */
	@action switchUser = (event: React.ChangeEvent<{}>, value: ComponentItem, reason: AutocompleteChangeReason): void => {
		const { personalViewIsBeingSelected } = this;
		this._currentSelectedUser = value;

		if (personalViewIsBeingSelected) {
			// if the current selected view is personal, select the first one by default
			const { selectCustomView } = this.#customViewDialogStore;
			const { defaultCustomView, filteredPersonalItem } = this;
			const targetView = filteredPersonalItem?.viewItems[0] || defaultCustomView;
			selectCustomView(targetView);
		}
	};

	constructor(
		personalItems: NonGlobalCustomView[],
		roleItems: NonGlobalCustomView[],
		systemWideItems: CustomViewItem[],
		favoriteItems: CustomViewItem[],
		customViewDialogStore: CustomViewDialogStore,
	) {
		super();
		makeObservable(this);
		this.personalItems = personalItems;
		this.initialRoleItems = roleItems;
		this.favoriteItems.replace(favoriteItems);
		this.systemWideItems.replace(systemWideItems);
		this.#customViewDialogStore = customViewDialogStore;

		this.trackReaction(reaction(
			() => this.selectedPreferenceView,
			(selectedPreferenceView) => this.refreshSelectedItems(selectedPreferenceView?.urlNode),
		));

		this.trackReaction(reaction(
			() => this.favoriteItems.length,
			() => this.refreshFavoriteItems(),
		));
	}
}